import {
  Agent,
  ChatSocketContextType,
  EventSchema,
  Message,
  SessionHistoryResponse,
} from '@monetize/types/app';
import { ENVIRONMENT } from '@monetize/ui/config';
import { useQueryClient } from '@tanstack/react-query';
import isArray from 'lodash/isArray';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';
import { getAppStore } from '../store/appStore';
import { v4 as uuidv4 } from 'uuid';
import { cpqServiceQueryKeys } from '../api/cpqService';
import { doCreateJWTToken } from '../api/usersService';
import {
  CHAT_CONTEXT_ID_REGEX,
  CHAT_ERROR_MESSAGE,
  CHAT_RECONNECT_FAILED_MESSAGE,
  CHAT_STILL_PROCESSING_MESSAGE,
  HELLO_MESSAGE_BY_AGENT,
  PREFIX_TO_ENTITY,
  QUOTE_REFRESH_TOOLS,
  RESPONSE_TAG,
  SUB_AGENT_TAGS,
} from '../constants/chat';
import { getQuoteEditRoute, QUOTE_BASE } from '../constants/routes';
import { currentUserIdState } from '../store/global.store';
import { useAuth } from './auth0';
import {
  countReconstructableMessages,
  fetchSessionHistoryWithRetry,
  mapHistoryMessages,
  pollForTurnCompletion,
} from './chatHistoryFetch';
import {
  ActivityAction,
  carryOverActivity,
  createTurnActivity,
  extractToolOutput,
  isToolUseBlock,
  reduceTurnActivity,
} from './chatActivity';
import {
  loadStoredChatSession,
  saveStoredChatSession,
} from './chatSessionStorage';
import {
  ChatConnectionError,
  delay,
  isDraftPreview,
  MAX_RECONNECT_ATTEMPTS,
  reconnectDelayMs,
  resolveStreamOutcome,
  settleSubAgentReplies,
  StreamOutcome,
} from './chatStream';
import { useFlags } from './launchDarkly';
import { logger } from './logger';

const ChatSocketContext = createContext<ChatSocketContextType | null>(null);
export const useChatSocket = () => {
  const context = useContext(ChatSocketContext);
  if (!context) {
    throw new Error('useChatSocket must be used within a ChatSocketProvider');
  }
  return context;
};

export const ChatSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [agent, setAgent] = useState<Agent>(Agent.MONETIZE_NOW_ASSISTANT);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: Date.now().toString(),
      content: HELLO_MESSAGE_BY_AGENT[agent],
      role: 'assistant',
      type: 'standard',
      timestamp: new Date(),
      isHelloMessage: true,
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  // True while reconciling with the backend after a dropped connection
  // (live mid-send) or while polling a still-running turn found on mount.
  const [isReconnecting, setIsReconnecting] = useState(false);
  // True only during the brief mount-time window between finding a saved
  // session and deciding what to render for it -- nothing conversation-
  // related should render while this is true, to avoid a jump once the
  // decision is made (see the mount effect below).
  const [isRestoringSession, setIsRestoringSession] = useState(false);
  const [sessionId, setSessionId] = useState(uuidv4());
  const abortControllerRef = useRef<AbortController | null>(null);
  // Separate from abortControllerRef (which only covers a live send) so the
  // Stop button can also cancel the mount-flow's own restore/poll -- without
  // this, disconnect() had nothing to abort during that window.
  const mountAbortControllerRef = useRef<AbortController | null>(null);
  // Mirrors `sessionId` for use in async code that shouldn't apply a
  // response if the session has since changed. Safe as a plain
  // effect-synced ref (unlike messagesRef below) because sessionId changes
  // are infrequent discrete events, not a per-token loop.
  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);
  // Source of truth for "the latest messages" outside of render, used to
  // persist the settled conversation from sendMessage's `finally` block.
  // A ref mirrored via a `useEffect(() => { messagesRef.current = messages
  // }, [messages])` is NOT sufficient here: effects only run on an actual
  // React commit, and the SSE token loop below can call setMessages many
  // times across awaited reads without React necessarily flushing a commit
  // in between, so the ref could still hold stale (e.g. pre-stream) data by
  // the time `finally` reads it. Updating the ref synchronously at every
  // mutation site (via `updateMessages`) makes it always accurate.
  const messagesRef = useRef(messages);
  const updateMessages = useCallback(
    (updater: (prev: Message[]) => Message[]) => {
      const next = updater(messagesRef.current);
      messagesRef.current = next;
      setMessages(next);
    },
    [],
  );
  const entityChangedRef = useRef(false);
  // Aliased: this scope already has its own `isLoading` (chat-turn-loading)
  // state above, distinct from Auth0's own "still resolving" flag.
  const { isAuthenticated, tenantId, isLoading: isAuthLoading } = useAuth();
  const userId = getAppStore().get(currentUserIdState);
  const navigate = useNavigate();
  const { useAiChat } = useFlags();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Adjusting state during render (React's documented escape hatch) so the
  // spinner can show on the *same* render pass the identity resolves/changes
  // on, rather than one paint late -- the mount effect below still does the
  // real async restoration work, but it only runs after a commit, which
  // would otherwise let stale/default content paint for a frame first.
  // `lastEvaluatedKey` starting at null means this also covers the initial
  // mount (once auth resolves), not just a later tenant/user/agent switch.
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | null>(null);
  const currentIdentityKey =
    isAuthLoading || !isAuthenticated || !tenantId || !userId
      ? null
      : `${tenantId}:${userId}:${agent}`;
  if (currentIdentityKey !== null && currentIdentityKey !== lastEvaluatedKey) {
    setLastEvaluatedKey(currentIdentityKey);
    const stored = loadStoredChatSession(tenantId, userId, agent);
    if (stored?.messages.some((m) => m.role === 'user')) {
      setIsRestoringSession(true);
    }
  }

  // Applies a fetched server history to the currently-*established* session
  // (not for initial establishment -- see the mount effect below, which
  // calls applySessionState directly instead since these guards don't fit
  // a first-time-establish scenario). Its only consumer is the live
  // reconnect path in sendMessage, which is why it carries these guards:
  // they protect correcting an already-showing session against a
  // concurrent send racing with a background history fetch.
  const applyServerHistory = useCallback(
    (
      history: SessionHistoryResponse,
      targetSessionId: string,
      currentMessages: Message[],
      allowedInFlightController: AbortController | null,
    ): boolean => {
      if (sessionIdRef.current !== targetSessionId) {
        return false; // session changed under us
      }
      if (abortControllerRef.current !== allowedInFlightController) {
        return false; // a newer/different send is active
      }
      // The turn being recovered was live until moments ago, so "now" is a
      // fair end time for its activity.
      const mapped = carryOverActivity(
        mapHistoryMessages(history.messages),
        currentMessages,
        Date.now(),
      );
      if (mapped.length === 0) {
        return false; // nothing committed server-side yet
      }
      // Server history never includes the synthetic hello message, error
      // bubbles, or hidden sub-agent messages -- compare against the local
      // count with those excluded, otherwise a legitimate, complete server
      // response can be rejected as "shorter than local" purely because of
      // this structural mismatch, not an actual regression.
      const localRealMessageCount =
        countReconstructableMessages(currentMessages);
      if (mapped.length < localRealMessageCount) {
        return false; // server data is less complete than what's already shown -- never regress a visible answer
      }
      messagesRef.current = mapped;
      setMessages(mapped);
      // Uses the closure's own tenantId/userId (current at apply time), not
      // parameters -- if either had changed since the fetch started, the
      // sessionIdRef guard above would already have caught it (a
      // tenant/user switch always changes the persisted sessionId too).
      if (isAuthenticated && tenantId && userId) {
        saveStoredChatSession(tenantId, userId, agent, {
          sessionId: targetSessionId,
          messages: mapped,
        });
      }
      return true;
    },
    [agent, isAuthenticated, tenantId, userId],
  );

  const isSubAgentEvent = useCallback((tags?: string[]) => {
    if (!tags) {
      return false;
    }
    return tags.some((tag) => SUB_AGENT_TAGS.includes(tag));
  }, []);

  const refreshRoute = useCallback(() => {
    navigate(
      {
        pathname: window.location.pathname,
        search: window.location.search,
      },
      {
        replace: true,
        state: { refresh: Date.now() },
      },
    );
  }, [navigate]);

  /**
   * Refetches the quote currently open in the app, if any. Everything the
   * quote page reads (offerings, prices, billing schedule, ...) is cached
   * under the `quotesById` prefix, so invalidating it refreshes the whole
   * page in place -- unlike `refreshRoute`, which remounts it and would
   * discard scroll position and any unsaved edits mid-turn.
   *
   * Reads `window.location` rather than the router's `location` so a
   * long-running stream can never act on a pathname the user has since
   * navigated away from.
   */
  const refreshOpenQuote = useCallback(() => {
    const quoteId = matchPath(
      { path: QUOTE_BASE, end: false },
      window.location.pathname,
    )?.params.quoteId;
    // `/quotes/new` and its sub-routes match the same pattern but have no
    // quote to refresh yet.
    if (!quoteId?.startsWith('quot_')) {
      return;
    }
    queryClient.invalidateQueries({
      queryKey: cpqServiceQueryKeys.quotesById(quoteId),
    });
  }, [queryClient]);

  const generateMessageId = () => uuidv4();
  const createMessage = useCallback(
    (
      content: string,
      role: 'user' | 'assistant',
      type: 'standard' | 'error' | 'final_result' = 'standard',
      error?: {
        type: 'error' | 'tool_error' | 'llm_error' | 'chain_error';
        message: string;
      },
      prompt?: string,
    ): Message => ({
      id: generateMessageId(),
      content,
      role,
      type,
      timestamp: new Date(),
      error,
      prompt,
    }),
    [],
  );

  const addMessage = useCallback(
    (message: Message) => {
      updateMessages((prev) => [...prev, message]);
    },
    [updateMessages],
  );

  /** Applies a stream-derived action to the activity on a turn's prompt. */
  const updateActivity = useCallback(
    (promptId: string, action: ActivityAction, replyCount = 0) => {
      updateMessages((prev) => {
        const index = prev.findIndex(({ id }) => id === promptId);
        const activity = prev[index]?.activity;
        if (!activity) {
          return prev;
        }
        const next = reduceTurnActivity(activity, action, {
          now: Date.now(),
          replyCount,
        });
        if (next === activity) {
          return prev;
        }
        const updated = [...prev];
        updated[index] = { ...prev[index], activity: next };
        return updated;
      });
    },
    [updateMessages],
  );

  const runStreamAttempt = useCallback(
    async (
      message: string,
      signal: AbortSignal,
      createdMessageIds: Set<string>,
      promptId: string,
    ): Promise<StreamOutcome> => {
      if (!tenantId || !userId) {
        return { kind: 'done' };
      }

      let receivedEnd = false;
      let streamedContent = false;
      let handledServerError = false;
      const assistantMessagesByStep = new Map<number, Message>();
      // Set when the backend names the message to display (a sub-agent that
      // ended the run itself); that message is the turn's complete reply.
      let responseMessageId: string | null = null;

      try {
        const token = await doCreateJWTToken('AI_ASSISTANT');

        const context = location.pathname.match(CHAT_CONTEXT_ID_REGEX);

        const res = await fetch(`${ENVIRONMENT.CHAT_URL}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-tenant-id': tenantId,
            'x-user-id': userId,
            Authorization: `Bearer ${token.token}`,
          },
          body: JSON.stringify({
            session_id: sessionId,
            agent,
            prompt: message,
            context: context
              ? {
                  application: 'mn-ui',
                  entities: context.map((id) => ({
                    id,
                    entity: PREFIX_TO_ENTITY[id.split('_')[0]],
                  })),
                }
              : undefined,
          }),
          signal,
        });

        if (!res.ok) {
          let bodyText = '';
          try {
            bodyText = await res.text();
          } catch {
            // body unreadable — fall through with status only
          }
          throw new ChatConnectionError(res.status, res.statusText, bodyText);
        }

        const reader = res.body?.getReader();
        const decoder = new TextDecoder();
        if (!reader) {
          throw new ChatConnectionError(
            res.status,
            res.statusText,
            'Response body is empty',
          );
        }

        let buffer = '';
        // Places any later stretch of work among the replies already shown.
        let visibleReplyCount = 0;

        let done = false;
        while (!done) {
          if (signal.aborted) {
            reader.cancel().catch(() => {});
            break;
          }

          const { value, done: readDone } = await reader.read();
          done = readDone;
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');

          for (let i = 0; i < lines.length - 1; i++) {
            const line = lines[i].trim();
            if (line.startsWith('data:')) {
              try {
                const jsonStr = line.replace(/^data:\s*/, '');
                const data = EventSchema.parse(JSON.parse(jsonStr));

                if ('token' in data) {
                  const step = data.step ?? 0;
                  const isSubAgent = isSubAgentEvent(data.tags);

                  if (isSubAgent) {
                    setIsThinking(true);
                  } else {
                    setIsThinking(false);
                  }

                  const toolCalls = isArray(data.token)
                    ? data.token.filter(isToolUseBlock)
                    : [];
                  if (toolCalls.length > 0) {
                    // Sub-agent text in the same model step as a tool call
                    // was narrating the call, not answering -- it belongs in
                    // the activity list, not in a reply that could later be
                    // promoted into view.
                    const narration = assistantMessagesByStep.get(step);
                    if (narration?.isSubAgentMessage) {
                      assistantMessagesByStep.delete(step);
                      createdMessageIds.delete(narration.id);
                      updateMessages((prev) =>
                        prev.filter(({ id }) => id !== narration.id),
                      );
                      updateActivity(
                        promptId,
                        {
                          type: 'narrated',
                          id: narration.id,
                          text: narration.content,
                        },
                        visibleReplyCount,
                      );
                    }
                    for (const { id, name } of toolCalls) {
                      updateActivity(
                        promptId,
                        { type: 'toolCalled', id, toolName: name },
                        visibleReplyCount,
                      );
                    }
                    continue;
                  }

                  if (!assistantMessagesByStep.has(step)) {
                    let text;
                    if (
                      isArray(data.token) &&
                      data.token.length === 1 &&
                      data.token[0].type === 'text'
                    ) {
                      text = data.token[0].text;
                    } else if (typeof data.token === 'string') {
                      text = data.token;
                    } else {
                      continue;
                    }
                    const newMsg = createMessage(text, 'assistant');
                    if (isSubAgent) {
                      newMsg.isSubAgentMessage = true;
                      if (isDraftPreview(newMsg)) {
                        updateActivity(
                          promptId,
                          { type: 'drafting' },
                          visibleReplyCount,
                        );
                      }
                    } else {
                      updateActivity(
                        promptId,
                        { type: 'replied' },
                        visibleReplyCount,
                      );
                      visibleReplyCount++;
                    }
                    assistantMessagesByStep.set(step, newMsg);
                    updateMessages((prev) => [...prev, newMsg]);
                    createdMessageIds.add(newMsg.id);
                    streamedContent = true;
                  } else {
                    const msg = assistantMessagesByStep.get(step)!;
                    let text;
                    if (
                      isArray(data.token) &&
                      data.token.length === 1 &&
                      data.token[0].type === 'text'
                    ) {
                      text = data.token[0].text;
                    } else if (typeof data.token === 'string') {
                      text = data.token;
                    } else {
                      continue;
                    }
                    const wasDraft = isDraftPreview(msg);
                    msg.content += text;
                    updateMessages((prev) =>
                      prev.map((m) =>
                        m.id === msg.id ? { ...m, content: msg.content } : m,
                      ),
                    );
                    if (
                      msg.isSubAgentMessage &&
                      !wasDraft &&
                      isDraftPreview(msg)
                    ) {
                      updateActivity(
                        promptId,
                        { type: 'drafting' },
                        visibleReplyCount,
                      );
                    }
                    streamedContent = true;
                  }
                } else if (
                  data.type === 'message' &&
                  data.tags?.includes(RESPONSE_TAG) &&
                  data.content
                ) {
                  setIsThinking(false);
                  const step = data.step ?? 0;
                  const streamedMsg = assistantMessagesByStep.get(step);
                  if (!streamedMsg || streamedMsg.isSubAgentMessage) {
                    // A draft (or a reply that never streamed) becomes the
                    // turn's reply, which ends the stretch of work above it.
                    updateActivity(
                      promptId,
                      { type: 'replied' },
                      visibleReplyCount,
                    );
                    visibleReplyCount++;
                  }
                  if (streamedMsg) {
                    // The backend's full text is authoritative over what was
                    // assembled from tokens (non-text chunks are skipped).
                    streamedMsg.content = data.content;
                    streamedMsg.isSubAgentMessage = false;
                    updateMessages((prev) =>
                      prev.map((m) =>
                        m.id === streamedMsg.id
                          ? {
                              ...m,
                              content: data.content,
                              isSubAgentMessage: false,
                            }
                          : m,
                      ),
                    );
                    responseMessageId = streamedMsg.id;
                  } else {
                    const newMsg = createMessage(data.content, 'assistant');
                    assistantMessagesByStep.set(step, newMsg);
                    updateMessages((prev) => [...prev, newMsg]);
                    createdMessageIds.add(newMsg.id);
                    responseMessageId = newMsg.id;
                  }
                  // The named reply is final, so clear the other drafts now
                  // rather than when the stream closes; until then an earlier,
                  // longer draft would be shown in its place.
                  const turnReplies = Array.from(
                    assistantMessagesByStep.values(),
                  );
                  updateMessages((prev) =>
                    settleSubAgentReplies(prev, turnReplies, true),
                  );
                  streamedContent = true;
                } else if (data.type === 'end') {
                  receivedEnd = true;
                } else if (data.type === 'model_start') {
                  updateActivity(
                    promptId,
                    { type: 'thinking' },
                    visibleReplyCount,
                  );
                } else if (data.type === 'model_end') {
                  // Narration is dropped from the step when its tool call
                  // arrives, so sub-agent text still here at the end of its
                  // step is the write-up.
                  const msg = assistantMessagesByStep.get(data.step ?? 0);
                  if (msg?.isSubAgentMessage && !msg.isWriteUp) {
                    msg.isWriteUp = true;
                    updateMessages((prev) =>
                      prev.map((m) =>
                        m.id === msg.id ? { ...m, isWriteUp: true } : m,
                      ),
                    );
                    updateActivity(
                      promptId,
                      { type: 'drafting' },
                      visibleReplyCount,
                    );
                  }
                } else if (data.type === 'tool_end') {
                  updateActivity(
                    promptId,
                    {
                      type: 'toolFinished',
                      id: data.tool_call_id,
                      toolName: data.tool_name,
                      output:
                        extractToolOutput(data.content) ??
                        data.output ??
                        undefined,
                    },
                    visibleReplyCount,
                  );
                  if (
                    data.tool_name &&
                    QUOTE_REFRESH_TOOLS.has(data.tool_name)
                  ) {
                    refreshOpenQuote();
                  }
                } else if (data.type === 'tool_error') {
                  // A failed call gets this instead of a tool_end.
                  updateActivity(
                    promptId,
                    {
                      type: 'toolFinished',
                      id: data.tool_call_id,
                      output: data.error,
                      failed: true,
                    },
                    visibleReplyCount,
                  );
                } else if (data.type === 'tool_start') {
                  updateActivity(
                    promptId,
                    {
                      type: 'toolCalled',
                      id: data.tool_call_id,
                      toolName: data.tool_name,
                      input: data.input,
                    },
                    visibleReplyCount,
                  );
                  if (data.tool_name === 'generate_redirect_signal') {
                    let toolInput;

                    try {
                      toolInput = JSON.parse(data.input);
                    } catch (err) {
                      try {
                        // Attempt to fix single quotes and re-parse
                        const corrected = data.input.replace(/'/g, '"');
                        toolInput = JSON.parse(corrected);
                      } catch (innerErr) {
                        logger.error('Failed to parse tool input:', innerErr);
                      }
                    }

                    if (toolInput?.entity_name && toolInput?.entity_id) {
                      entityChangedRef.current = true;
                    }

                    if (
                      toolInput?.entity_name === 'quote' &&
                      toolInput?.entity_id
                    ) {
                      navigate(getQuoteEditRoute(toolInput.entity_id), {
                        state: { refresh: Date.now() },
                      });
                    }
                  }
                } else if (data.type === 'usage_limit_exceeded') {
                  const errorMsg = createMessage(
                    data.message,
                    'assistant',
                    'error',
                    {
                      type: 'error',
                      message: data.message,
                    },
                    message,
                  );
                  addMessage(errorMsg);
                  handledServerError = true;
                } else if (data.type === 'error') {
                  const errorMsg = createMessage(
                    CHAT_ERROR_MESSAGE,
                    'assistant',
                    'error',
                    {
                      type: data.type,
                      message: data.error,
                    },
                    message,
                  );
                  addMessage(errorMsg);
                  handledServerError = true;
                }
              } catch (e) {
                logger.error('chatSocket: Invalid SSE data', e);
              }
            }
          }

          buffer = lines[lines.length - 1]; // preserve incomplete line
        }

        if (signal.aborted) {
          return { kind: 'done' };
        }

        // A named reply means the run finished, even if the connection
        // closed before `end`; recovering would resend a completed turn.
        return resolveStreamOutcome({
          receivedEnd: receivedEnd || responseMessageId !== null,
          streamedContent,
          handledServerError,
        });
      } catch (err) {
        return resolveStreamOutcome({
          receivedEnd: receivedEnd || responseMessageId !== null,
          streamedContent,
          handledServerError,
          error: err,
        });
      } finally {
        // Runs however the stream ended -- including Stop, where aborting
        // the fetch rejects the pending read -- so a draft the user was
        // reading never vanishes with the turn. The reply is complete when the
        // backend named it, or when the stream reached its `end` event without
        // a server error.
        const isComplete =
          responseMessageId !== null || (receivedEnd && !handledServerError);
        const turnReplies = Array.from(assistantMessagesByStep.values());
        if (
          turnReplies.length > 0 &&
          turnReplies.every(({ isSubAgentMessage }) => isSubAgentMessage)
        ) {
          logger.warn(
            'chatSocket: No supervisor response received, promoting sub-agent messages',
          );
        }
        updateMessages((prev) =>
          settleSubAgentReplies(prev, turnReplies, isComplete),
        );
      }
    },
    [
      tenantId,
      userId,
      addMessage,
      createMessage,
      location.pathname,
      sessionId,
      agent,
      navigate,
      isSubAgentEvent,
      refreshOpenQuote,
      updateMessages,
      updateActivity,
    ],
  );

  const sendMessage = useCallback(
    async (message: string) => {
      if (
        !isAuthenticated ||
        !tenantId ||
        !userId ||
        !useAiChat ||
        isRestoringSession
      ) {
        return;
      }

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const { signal } = controller;

      setIsLoading(true);
      const prompt: Message = {
        ...createMessage(message, 'user'),
        activity: createTurnActivity(Date.now()),
      };
      addMessage(prompt);
      // Persist immediately, before waiting on the reply -- otherwise a
      // reload during this turn's "thinking" window would find local
      // storage still holding the *previous* (already-concluded) state,
      // with no trace this session has an active turn at all. The mount
      // flow's "does this session have anything to reconcile" check reads
      // straight from local storage, so without this it would wrongly
      // treat an in-progress first turn as a brand-new, never-sent
      // session and skip the /history check that would have found it
      // still running server-side. Only the user's own message is
      // persisted here -- the assistant's reply streams in via
      // updateMessages below, which never persists mid-stream, so no
      // partial/incomplete answer is ever written to storage.
      saveStoredChatSession(tenantId, userId, agent, {
        sessionId,
        messages: messagesRef.current,
      });

      entityChangedRef.current = false;

      let outcome: StreamOutcome = { kind: 'done' };

      try {
        for (let attempt = 0; attempt <= MAX_RECONNECT_ATTEMPTS; attempt++) {
          setIsReconnecting(false); // actively attempting/streaming this iteration
          const createdMessageIds = new Set<string>();
          outcome = await runStreamAttempt(
            message,
            signal,
            createdMessageIds,
            prompt.id,
          );

          if (signal.aborted) {
            outcome = { kind: 'done' };
            break;
          }
          if (outcome.kind !== 'lost') {
            break;
          }

          setIsReconnecting(true); // connection dropped -- entering recovery flow

          // Check what's actually happening server-side before resending
          // the same prompt (which the backend will reject with 409 if the
          // original turn is still running, per BP-18004's active_sessions
          // guard -- now itself reclassified as 'lost' in
          // resolveStreamOutcome, so a resend that hits it loops back
          // through this same check rather than dead-ending on a raw
          // error). Uses the retrying variant -- a lone transient failure
          // on this check alone shouldn't immediately force a resend,
          // which is the more disruptive fallback.
          const history = await fetchSessionHistoryWithRetry(
            sessionId,
            tenantId,
            userId,
            signal,
          );
          if (signal.aborted) {
            outcome = { kind: 'done' };
            break;
          }

          if (history?.turn_status === 'completed') {
            if (
              applyServerHistory(
                history,
                sessionId,
                messagesRef.current,
                controller,
              )
            ) {
              outcome = { kind: 'done' };
              break;
            }
            // mapped data didn't clear the "don't regress" bar -- fall
            // through to resend below as a safety net
          } else if (history?.turn_status === 'running') {
            // Confirmed still running -- a healthy 200, not a connection
            // problem, so show the normal generating indicator
            // (isThinking) rather than isReconnecting, which is reserved
            // for when the status check itself is failing and being
            // retried (see the onCheckResult callback below).
            setIsReconnecting(false);
            setIsThinking(true);
            const completedHistory = await pollForTurnCompletion(
              sessionId,
              tenantId,
              userId,
              signal,
              (failed) => {
                // fetchSessionHistory doesn't accept/forward this signal to
                // its own fetch() call, so an in-flight check can still
                // resolve and invoke this callback after `signal` has
                // already been aborted (e.g. a newer send superseded this
                // one) -- without this guard, that stray callback could
                // clobber isReconnecting/isThinking state that belongs to
                // whatever now-current attempt or send has already reset
                // them via its own finally block.
                if (signal.aborted) {
                  return;
                }
                setIsReconnecting(failed);
                setIsThinking(!failed);
              },
            );
            if (signal.aborted) {
              outcome = { kind: 'done' };
              break;
            }
            if (
              completedHistory &&
              applyServerHistory(
                completedHistory,
                sessionId,
                messagesRef.current,
                controller,
              )
            ) {
              outcome = { kind: 'done' };
              break;
            }
            // Do NOT fall through to the generic resend path below. We've
            // just confirmed the turn was actively running server-side;
            // resending the same prompt would immediately hit the same 409
            // from BP-18004's active_sessions guard (the whole problem
            // this fix exists to avoid), regardless of whether polling
            // gave up because it's still running or because a poll's fetch
            // itself failed transiently. Terminate distinctly.
            outcome = {
              kind: 'failed',
              message: CHAT_STILL_PROCESSING_MESSAGE,
              detail:
                'turn confirmed running server-side; polling for completion did not resolve in time',
            };
            break;
          }
          // history fetch failed even after fetchSessionHistoryWithRetry's
          // own retries (404, or a sustained/repeated network failure --
          // turn status genuinely undeterminable) -- fall through to
          // resend below; this is the one case where we never confirmed a
          // run was in progress, so resending is safe.

          if (attempt === MAX_RECONNECT_ATTEMPTS) {
            outcome = {
              kind: 'failed',
              message: CHAT_RECONNECT_FAILED_MESSAGE,
              detail: outcome.detail,
            };
            break;
          }

          logger.warn(
            `chatSocket: connection lost, restarting session stream (attempt ${
              attempt + 1
            }/${MAX_RECONNECT_ATTEMPTS})`,
          );

          if (createdMessageIds.size > 0) {
            updateMessages((prev) =>
              prev.filter(({ id }) => !createdMessageIds.has(id)),
            );
          }
          updateActivity(prompt.id, { type: 'restarted' });
          setIsThinking(false);

          await delay(reconnectDelayMs(attempt + 1), signal);

          if (signal.aborted) {
            outcome = { kind: 'done' };
            break;
          }
          // isReconnecting stays true through the delay; reset to false at
          // the top of the next iteration right before the resent
          // runStreamAttempt call.
        }

        if (outcome.kind === 'failed') {
          logger.error('chatSocket: SSE connection failed', outcome.detail);
          addMessage(
            createMessage(
              outcome.message,
              'assistant',
              'error',
              { type: 'error', message: outcome.detail },
              message,
            ),
          );
        }
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
        if (entityChangedRef.current) {
          refreshRoute();
        }
        updateActivity(prompt.id, {
          type: 'finished',
          stopped: signal.aborted,
        });
        setIsLoading(false);
        setIsThinking(false);
        setIsReconnecting(false);
        if (isAuthenticated && tenantId && userId) {
          saveStoredChatSession(tenantId, userId, agent, {
            sessionId,
            messages: messagesRef.current,
          });
        }
      }
    },
    [
      isAuthenticated,
      tenantId,
      userId,
      useAiChat,
      isRestoringSession,
      addMessage,
      createMessage,
      runStreamAttempt,
      refreshRoute,
      agent,
      sessionId,
      updateMessages,
      updateActivity,
      applyServerHistory,
    ],
  );

  const retryMessage = useCallback(
    async (messageId: string) => {
      const errorMsg = messages.find((m) => m.id === messageId);
      if (!errorMsg?.prompt) {
        return;
      }

      const errorIdx = messages.findIndex((m) => m.id === messageId);
      let userMsgIdx = -1;
      for (let i = errorIdx - 1; i >= 0; i--) {
        if (messages[i].role === 'user') {
          userMsgIdx = i;
          break;
        }
      }

      const removeIds = new Set(
        messages
          .slice(userMsgIdx === -1 ? errorIdx : userMsgIdx, errorIdx + 1)
          .map((m) => m.id),
      );
      updateMessages((prev) => prev.filter((m) => !removeIds.has(m.id)));

      await sendMessage(errorMsg.prompt);
    },
    [messages, sendMessage, updateMessages],
  );

  const disconnect = useCallback(async () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    mountAbortControllerRef.current?.abort();
    mountAbortControllerRef.current = null;
    setIsLoading(false);
    setIsThinking(false);

    if (isAuthenticated && tenantId && userId && sessionId) {
      try {
        const token = await doCreateJWTToken('AI_ASSISTANT');

        await fetch(`${ENVIRONMENT.CHAT_URL}/chat/cancel`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-tenant-id': tenantId,
            'x-user-id': userId,
            Authorization: `Bearer ${token.token}`,
          },
          body: JSON.stringify({
            session_id: sessionId,
            agent,
          }),
        });
      } catch (err) {
        logger.error('chatSocket: Failed to call cancel endpoint', err);
      }
    }
  }, [isAuthenticated, tenantId, userId, sessionId, agent]);

  const applySessionState = useCallback(
    (next: { sessionId: string; messages: Message[] }) => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      messagesRef.current = next.messages;
      setMessages(next.messages);
      setSessionId(next.sessionId);
      setIsLoading(false);
      setIsThinking(false);
      if (isAuthenticated && tenantId && userId) {
        saveStoredChatSession(tenantId, userId, agent, next);
      }
    },
    [isAuthenticated, tenantId, userId, agent],
  );

  const reset = useCallback(() => {
    applySessionState({
      sessionId: uuidv4(),
      messages: [
        {
          id: Date.now().toString(),
          content: HELLO_MESSAGE_BY_AGENT[agent],
          role: 'assistant',
          timestamp: new Date(),
          type: 'standard',
          isHelloMessage: true,
        },
      ],
    });
  }, [agent, applySessionState]);

  // On mount/identity-resolution, check the server *before* rendering any
  // conversation content, rather than showing local storage immediately
  // and silently reconciling in the background -- the latter would jump
  // visibly whenever local was incomplete (e.g. reloading while the prior
  // turn was still generating), which is exactly the case this flow exists
  // to handle cleanly instead.
  useEffect(() => {
    if (isAuthLoading) {
      // Auth0 hasn't resolved yet; isAuthenticated/tenantId aren't
      // trustworthy, so don't reset or hydrate on incomplete information.
      return;
    }
    if (!isAuthenticated) {
      reset();
      setIsRestoringSession(false);
      return;
    }
    if (!tenantId || !userId) {
      // Authenticated, but tenant/user identity is still resolving; wait
      // rather than wiping the current state based on incomplete info.
      return;
    }

    const stored = loadStoredChatSession(tenantId, userId, agent);
    if (!stored) {
      reset();
      setIsRestoringSession(false);
      return;
    }

    // A session that has never had a user message sent (e.g. right after
    // "New Chat" -> reset(), which persists a fresh session_id with only
    // the synthetic hello message) has no server-side checkpoint to
    // reconcile with -- calling /history for it is a wasted round trip
    // (and, if the backend is briefly unreachable, a needless multi-second
    // retry/loading flash) for a session that could not possibly have any
    // history yet.
    const hasSentMessage = stored.messages.some((m) => m.role === 'user');
    if (!hasSentMessage) {
      applySessionState(stored);
      setIsRestoringSession(false);
      return;
    }

    let cancelled = false;
    setIsRestoringSession(true);
    const mountController = new AbortController();
    mountAbortControllerRef.current = mountController;

    // `cancelled` only flips when this effect instance is superseded by a
    // dependency change (e.g. a tenant switch) -- it does NOT cover the
    // user manually calling reset() ("New Chat") mid-poll, since reset()
    // doesn't touch any of this effect's own dependencies. sessionIdRef
    // catches that case too, but ONLY once this flow has actually
    // established `stored.sessionId` into session state via
    // applySessionState(stored) below -- comparing against it any earlier
    // would always look "stale" (sessionIdRef still holds whatever
    // pre-existing/initial session id was current before this restoration
    // ever ran). Without this check (once applicable), a stale poll for
    // the *old* session could keep toggling isReconnecting/isThinking for
    // a brand-new chat, or worse, its eventual "completed" result could
    // silently overwrite the new chat's unrelated conversation with the
    // old session's late-arriving answer. mountController.signal.aborted
    // covers a 4th case neither of the above does: disconnect() (Stop)
    // aborts the controller without touching `cancelled` or `sessionId` --
    // without checking it here too, a Stop click could still surface the
    // "polling exhausted" recovery bubble below, as if the backend had
    // gone unreachable on its own rather than the user cancelling it.
    const isStaleSession = () =>
      cancelled ||
      sessionIdRef.current !== stored.sessionId ||
      mountController.signal.aborted;

    (async () => {
      const history = await fetchSessionHistoryWithRetry(
        stored.sessionId,
        tenantId,
        userId,
        mountController.signal,
      );
      if (cancelled) {
        return;
      }

      if (!history) {
        applySessionState(stored); // can't reach the server -- best-effort local
        setIsRestoringSession(false);
        return;
      }

      if (history.turn_status === 'running') {
        applySessionState(stored); // show what's committed locally now (the question, no answer yet)
        setIsRestoringSession(false);
        // A confirmed 200 + "running" is a healthy signal the turn is
        // genuinely still being worked on -- shows the normal generating
        // indicator (isThinking), not isReconnecting, which is reserved
        // for when the status check itself is failing and being retried
        // (see the onCheckResult callback below).
        setIsThinking(true);
        // Also flag isLoading, matching how a live send naturally keeps it
        // true throughout its own reconnect window -- MessageInput.tsx
        // already disables sending on isLoading, so this is what prevents
        // the user from sending a new message while this restored
        // session's prior turn is still being waited on.
        setIsLoading(true);
        const completed = await pollForTurnCompletion(
          stored.sessionId,
          tenantId,
          userId,
          mountController.signal,
          (failed) => {
            // mountController.signal stops this poll on an identity change
            // (cleanup below) or a Stop click (disconnect()), but not on a
            // manual reset() ("New Chat") mid-poll, since reset() doesn't
            // touch mountAbortControllerRef -- isStaleSession() still
            // catches that case via sessionIdRef. Without this guard, a
            // now-abandoned poll's in-flight fetch could still resolve and
            // keep firing isReconnecting/isThinking toggles for whatever
            // *new* session is now current, corrupting its state every ~5s
            // until the poll finally exhausts on its own.
            if (isStaleSession()) {
              return;
            }
            setIsReconnecting(failed);
            setIsThinking(!failed);
          },
        );
        if (isStaleSession()) {
          return;
        }
        setIsReconnecting(false);
        setIsThinking(false);
        setIsLoading(false);
        if (completed) {
          // Polling just watched this turn finish, so "now" is a fair end
          // time for the activity it was showing.
          const mapped = carryOverActivity(
            mapHistoryMessages(completed.messages),
            stored.messages,
            Date.now(),
          );
          if (
            mapped.length > 0 &&
            mapped.length >= countReconstructableMessages(stored.messages)
          ) {
            applySessionState({
              sessionId: stored.sessionId,
              messages: mapped,
            });
          }
        } else {
          // Polling exhausted -- since pollForTurnCompletion only counts
          // *failed* fetches against MAX_POLL_ATTEMPTS (a confirmed 200 +
          // "running" never does), reaching this branch means the backend
          // became unreachable, not just slow. Surface an error bubble
          // with a manual Retry action -- reusing the exact same
          // prompt/retryMessage mechanism as the live-reconnect exhaustion
          // case below -- rather than leaving the user stuck on a
          // silently stale view with no recovery short of a full reload.
          const lastUserMessage = [...stored.messages]
            .reverse()
            .find((m) => m.role === 'user');
          // Don't stack a second identical bubble if a *previous* restore
          // attempt already left one here (e.g. the backend is down across
          // several reloads in a row) -- stored.messages already includes
          // it via applySessionState(stored) above. Scoped to only the last
          // message, not the whole history: scanning all of it would also
          // suppress this bubble for a later, unrelated turn that happens
          // to fail after an *older* turn already showed one.
          const alreadyShowingStillProcessing =
            stored.messages[stored.messages.length - 1]?.content ===
            CHAT_STILL_PROCESSING_MESSAGE;
          if (lastUserMessage && !alreadyShowingStillProcessing) {
            addMessage(
              createMessage(
                CHAT_STILL_PROCESSING_MESSAGE,
                'assistant',
                'error',
                {
                  type: 'error',
                  message:
                    'polling for turn completion failed repeatedly while restoring this session',
                },
                lastUserMessage.content,
              ),
            );
            if (isAuthenticated && tenantId && userId) {
              saveStoredChatSession(tenantId, userId, agent, {
                sessionId: stored.sessionId,
                messages: messagesRef.current,
              });
            }
          }
        }
        return;
      }

      // turn_status === 'completed' (or any other value): prefer server
      // data, falling back to local if it's somehow less complete (same
      // don't-regress reasoning as applyServerHistory, inlined here since
      // there's no already-applied session to guard against clobbering
      // yet).
      const mapped = carryOverActivity(
        mapHistoryMessages(history.messages),
        stored.messages,
      );
      const localRealCount = countReconstructableMessages(stored.messages);
      applySessionState({
        sessionId: stored.sessionId,
        messages:
          mapped.length > 0 && mapped.length >= localRealCount
            ? mapped
            : stored.messages,
      });
      setIsRestoringSession(false);
    })();

    return () => {
      cancelled = true;
      // Belt-and-suspenders on top of isStaleSession(): stops the in-flight
      // fetch/poll itself (via fetchSessionHistory's signal support), not
      // just this closure's own view of whether it's stale.
      mountController.abort();
      if (mountAbortControllerRef.current === mountController) {
        mountAbortControllerRef.current = null;
      }
      // Unconditionally clear all four flags this effect instance might
      // have set, regardless of which stage cancellation happens at.
      // Without this, the `if (cancelled) return;` checks above return
      // *before* reaching whichever setIsRestoringSession(false)/
      // setIsReconnecting(false)/setIsThinking(false)/setIsLoading(false)
      // call would have run -- and nothing else ever clears them (a
      // superseding effect instance's own applySessionState()/reset() only
      // resets isLoading, not the other three). Without this cleanup, any
      // cancellation mid-check (e.g. a tenant switch while still polling)
      // leaves the UI permanently stuck. Safe to call unconditionally: if
      // these were already false, setting them false again is a no-op.
      setIsRestoringSession(false);
      setIsReconnecting(false);
      setIsThinking(false);
      setIsLoading(false);
    };
  }, [
    agent,
    reset,
    isAuthenticated,
    tenantId,
    userId,
    applySessionState,
    isAuthLoading,
    addMessage,
    createMessage,
  ]);

  return (
    <ChatSocketContext.Provider
      value={{
        agent,
        setAgent,
        messages,
        isLoading,
        isThinking,
        isReconnecting,
        isRestoringSession,
        sessionId,
        sendMessage,
        retryMessage,
        reset,
        disconnect,
      }}
    >
      {children}
    </ChatSocketContext.Provider>
  );
};
